<app-section-grid *ngIf="!embeddedMode" [subSection]="'Edit User'"></app-section-grid>

<div class="relative w-full mx-auto p-10 bg-white rounded-3xl mb-5" [class.border]="hasBorder"
    [class.border-gray-200]="hasBorder" style="max-width: 760px;">

    <form [formGroup]="editForm" (ngSubmit)="onSubmit()">
        <!-- Header -->
        <div class="text-center mb-4">
            <h1 class="text-3xl font-bold text-gray-800 tracking-tight">
                Edit User Details
            </h1>
            <p class="mt-2 text-gray-500">
                Update the user information below.
            </p>
        </div>

        <div *ngIf="errorMessage" class="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
            <div class="flex items-center">
                <svg class="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clip-rule="evenodd" />
                </svg>
                <span class="text-red-700 text-sm">{{ errorMessage }}</span>
            </div>
        </div>

        <!-- Success/Error Messages -->
        <div *ngIf="successMessage" class="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl">
            <div class="flex items-center">
                <svg class="w-5 h-5 text-green-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                        clip-rule="evenodd" />
                </svg>
                <span class="text-green-700 text-sm">{{ successMessage }}</span>
            </div>
        </div>

        <!-- Fields -->
        <div class="grid grid-cols-12 gap-6">

            <!-- Email -->
            <div class="col-span-12 lg:col-span-12">
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                    📧 Email <small class="text-red-300">(required)</small>
                </label>

                <div class="relative">
                    <input formControlName="userEmail" type="email" autocomplete="off" readonly
                        onfocus="this.removeAttribute('readonly');" class="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors" />
                </div>

                <p *ngIf="editForm.get('userEmail')?.hasError('required') && editForm.get('userEmail')?.touched"
                    class="text-red-500 text-xs mt-1">
                    Email is required.
                </p>

                <p *ngIf="editForm.get('userEmail')?.hasError('userEmail') && editForm.get('userEmail')?.touched"
                    class="text-red-500 text-xs mt-1">
                    Please enter a valid email address.
                </p>
            </div>

            <!-- First Name -->
            <div class="col-span-12 lg:col-span-6">
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                    🏷 First Name <small class="text-red-300">(required)</small>
                </label>

                <input formControlName="firstName" class="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors" />

                <p *ngIf="editForm.get('firstName')?.hasError('required') && editForm.get('firstName')?.touched"
                    class="text-red-500 text-xs mt-1">
                    First Name is required.
                </p>
            </div>

            <!-- Last Name -->
            <div class="col-span-12 lg:col-span-6">
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                    🏷 Last Name <small class="text-red-300">(required)</small>
                </label>

                <input formControlName="lastName" class="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors" />

                <p *ngIf="editForm.get('lastName')?.hasError('required') && editForm.get('lastName')?.touched"
                    class="text-red-500 text-xs mt-1">
                    Last Name is required.
                </p>
            </div>

            <!-- Phone -->
            <div class="col-span-12 lg:col-span-4">
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                    📞 Phone <small class="text-red-300">(required)</small>
                </label>

                <input formControlName="phoneNumber" class="w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                       focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors" />

                <p *ngIf="editForm.get('phoneNumber')?.hasError('required') && editForm.get('phoneNumber')?.touched"
                    class="text-red-500 text-xs mt-1">
                    Phone Number is required.
                </p>
            </div>

            <!-- Role -->
            <div class="col-span-12 lg:col-span-4">
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                    👥 Role <small class="text-red-300">(required)</small>
                </label>

                <select formControlName="userGroupID" class="cursor-pointer w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors">
                    <option value="">Select Role</option>
                    <option *ngFor="let role of roles" [value]="role.value">
                        {{ role.label }}
                    </option>
                </select>

                <p *ngIf="editForm.get('userGroupID')?.hasError('required') && editForm.get('userGroupID')?.touched"
                    class="text-red-500 text-xs mt-1">
                    Role is required.
                </p>
            </div>

            <!-- Status -->
            <div class="col-span-12 lg:col-span-4">
                <label class="block text-sm font-semibold text-gray-700 mb-2">
                    📊 Status <small class="text-red-300">(required)</small>
                </label>

                <select formControlName="isActive" class="cursor-pointer w-full border border-gray-300 rounded-xl px-4 py-2.5 bg-gray-50 text-gray-700
                        focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm shadow-sm transition-colors">
                    <option value="">Select Status</option>
                    <option *ngFor="let status of statuses" [value]="status.value">
                        {{ status.label }}
                    </option>
                </select>

                <p *ngIf="editForm.get('isActive')?.hasError('required') && editForm.get('isActive')?.touched"
                    class="text-red-500 text-xs mt-1">
                    Status is required.
                </p>
            </div>

        </div>

        <!-- Action Buttons -->
        <div class="mt-10 pt-6 border-t border-gray-200 flex justify-end space-x-4">
            <!-- Cancel Button -->
            <button type="button" (click)="onCancel()" [disabled]="isSubmitting" class="px-6 py-2.5 border border-gray-300 rounded-xl text-sm font-semibold text-gray-700 
                           hover:bg-gray-50 hover:border-gray-400 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed">
                Cancel
            </button>

            <!-- Submit Button -->
            <button type="submit" [disabled]="editForm.invalid || isSubmitting" class="px-6 py-2.5 bg-blue-600 text-white rounded-xl text-sm font-semibold 
                           hover:bg-blue-700 focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed">

                <!-- Loading Spinner -->
                <span *ngIf="isSubmitting" class="flex items-center">
                    <svg class="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg"
                        fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4">
                        </circle>
                        <path class="opacity-75" fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z">
                        </path>
                    </svg>
                    Saving...
                </span>

                <!-- Normal State -->
                <span *ngIf="!isSubmitting">
                    Save Changes
                </span>
            </button>
        </div>

    </form>

</div>

<app-footer-grid *ngIf="!embeddedMode"></app-footer-grid>